import type { AssigneeType } from '../types';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

const ASSIGNEE_TYPES: AssigneeType[] = [
    'employee',
    'manager',
    'hr',
    'it',
    'specific_user',
];

interface StepEditorProps {
    step: {
        title: string;
        description: string;
        assignee_type: AssigneeType;
        specific_user_id: string;
        due_offset_days: number;
        evidence_required: boolean;
        depends_on_step_order: number | null;
    };
    index: number;
    totalSteps: number;
    onChange: (patch: Record<string, unknown>) => void;
}

export function StepEditor({
    step,
    index,
    totalSteps,
    onChange,
}: StepEditorProps) {
    return (
        <div className="grid gap-3 sm:grid-cols-2">
            {/* Title */}
            <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium">Title</label>
                <Input
                    value={step.title}
                    onChange={(e) => onChange({ title: e.target.value })}
                    placeholder="e.g. Set up workstation"
                />
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium">Description</label>
                <Input
                    value={step.description}
                    onChange={(e) => onChange({ description: e.target.value })}
                    placeholder="Optional instructions"
                />
            </div>

            {/* Assignee type */}
            <div>
                <label className="mb-1 block text-xs font-medium">Assignee Role</label>
                <Select
                    value={step.assignee_type}
                    onValueChange={(v) => onChange({ assignee_type: v })}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {ASSIGNEE_TYPES.map((a) => (
                            <SelectItem key={a} value={a}>
                                {a.replace(/_/g, ' ')}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Specific user (conditional) */}
            {step.assignee_type === 'specific_user' && (
                <div>
                    <label className="mb-1 block text-xs font-medium">User ID</label>
                    <Input
                        value={step.specific_user_id}
                        onChange={(e) => onChange({ specific_user_id: e.target.value })}
                        placeholder="User UUID"
                    />
                </div>
            )}

            {/* Due offset */}
            <div>
                <label className="mb-1 block text-xs font-medium">
                    Due Offset (days from start)
                </label>
                <Input
                    type="number"
                    min={0}
                    value={step.due_offset_days}
                    onChange={(e) =>
                        onChange({ due_offset_days: parseInt(e.target.value, 10) || 0 })
                    }
                />
            </div>

            {/* Dependency */}
            <div>
                <label className="mb-1 block text-xs font-medium">
                    Depends On Step #
                </label>
                <Select
                    value={
                        step.depends_on_step_order !== null
                            ? String(step.depends_on_step_order)
                            : 'none'
                    }
                    onValueChange={(v) =>
                        onChange({
                            depends_on_step_order: v === 'none' ? null : Number(v),
                        })
                    }
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {Array.from({ length: totalSteps }, (_, i) => i + 1)
                            .filter((n) => n !== index + 1)
                            .map((n) => (
                                <SelectItem key={n} value={String(n)}>
                                    Step {n}
                                </SelectItem>
                            ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Evidence required */}
            <div className="flex items-center gap-2 sm:col-span-2">
                <input
                    type="checkbox"
                    id={`ev-${index}`}
                    checked={step.evidence_required}
                    onChange={(e) => onChange({ evidence_required: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor={`ev-${index}`} className="text-sm">
                    Evidence / attachment required to complete this step
                </label>
            </div>
        </div>
    );
}